import {Canvas, FabricObject} from 'fabric';

export interface GridSettings {
    // Draw the grid behind the board.
    show: boolean;
    // Land moved objects on the nearest grid line.
    snap: boolean;
    // Spacing in board units.
    size: number;
}

export const gridSizes = [10, 20, 25, 50, 100];

const storageKey = 'whiteboardGrid';

const fallback: GridSettings = {show: false, snap: false, size: 20};

// The grid is a per-browser preference, like the sticky note defaults: it is a guide, not part of the board.
export const loadGridSettings = (): GridSettings => {
    try {
        const stored = window.localStorage.getItem(storageKey);
        if (!stored) {
            return fallback;
        }
        const parsed = JSON.parse(stored);
        return {
            show: parsed.show === true,
            snap: parsed.snap === true,
            size: gridSizes.includes(parsed.size) ? parsed.size : fallback.size
        };
    } catch {
        return fallback;
    }
};

export const saveGridSettings = (settings: GridSettings) => {
    try {
        window.localStorage.setItem(storageKey, JSON.stringify(settings));
    } catch {
        // A private window or full storage: the grid just isn't remembered.
    }
};

export const snapToGrid = (value: number, size: number) =>
    Math.round(value / size) * size;

// Grid lines closer together than this on screen are noise, so only every fifth is drawn.
const MIN_LINE_SPACING_PX = 8;

/*
 * The grid, drawn as the canvas's background image so it sits over the
 * background colour and under every object, and follows the viewport (Fabric
 * draws the background image through the viewport transform by default).
 * It has no size of its own: each render covers whatever part of the board
 * is on screen, at one device pixel wide whatever the zoom.
 */
export class GridBackground extends FabricObject {
    static type = 'grid-background';

    private readonly board: Canvas;
    private size: number;

    constructor(board: Canvas, size: number) {
        super({
            left: 0,
            top: 0,
            width: 0,
            height: 0,
            originX: 'left',
            originY: 'top',
            strokeWidth: 0,
            objectCaching: false,
            selectable: false,
            evented: false,
            excludeFromExport: true
        });
        this.board = board;
        this.size = size;
    }

    setSize(size: number) {
        this.size = size;
    }

    // Never cached: what it draws depends on the viewport, not on itself.
    shouldCache(): boolean {
        return false;
    }

    // Fabric skips an object with no size; this one is all size.
    isNotVisible(): boolean {
        return false;
    }

    isOnScreen(): boolean {
        return true;
    }

    _render(ctx: CanvasRenderingContext2D) {
        const zoom = this.board.getZoom();
        const coords = this.board.vptCoords;
        if (!coords || zoom <= 0) {
            return;
        }

        const every =
            this.size * zoom < MIN_LINE_SPACING_PX ? this.size * 5 : this.size;
        const major = every * 5;

        const left = Math.floor(coords.tl.x / every) * every;
        const top = Math.floor(coords.tl.y / every) * every;
        const right = coords.br.x;
        const bottom = coords.br.y;

        ctx.save();
        ctx.lineWidth = 1 / zoom;

        for (const [alpha, isMajor] of [
            [0.08, false],
            [0.16, true]
        ] as const) {
            ctx.strokeStyle = `rgba(0, 0, 0, ${alpha})`;
            ctx.beginPath();
            for (let x = left; x <= right; x += every) {
                if ((x % major === 0) === isMajor) {
                    ctx.moveTo(x, top);
                    ctx.lineTo(x, bottom);
                }
            }
            for (let y = top; y <= bottom; y += every) {
                if ((y % major === 0) === isMajor) {
                    ctx.moveTo(left, y);
                    ctx.lineTo(right, y);
                }
            }
            ctx.stroke();
        }

        ctx.restore();
    }
}
